import { useEffect, useState } from "react";

export function useTheme() {
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem("theme") || (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light");
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    localStorage.setItem("theme", theme);
  }, [theme]);

  useEffect(() => {
    const f = (e) => setTheme(e.detail);
    window.addEventListener("theme:set", f);
    return () => window.removeEventListener("theme:set", f);
  }, []);

  return { theme, setTheme, toggle: () => setTheme((t) => (t === "dark" ? "light" : "dark")) };
}

export const EVENT_META = {
  watering: { label: "Watered", color: "#0A84FF" },
  fertilizing: { label: "Fertilized", color: "#30D158" },
  repotting: { label: "Repotted", color: "#AF52DE" },
  pruning: { label: "Pruned", color: "#FF9F0A" },
  pest_treatment: { label: "Pest treated", color: "#FF375F" },
  grow_light: { label: "Grow light", color: "#E8A100" },
  disease: { label: "Disease noted", color: "#FF453A" },
  photo: { label: "Photo added", color: "#64D2FF" },
  note: { label: "Note", color: "#8E8E93" },
  critical: { label: "Acute event", color: "#FF3B30" },
  growth: { label: "Progress logged", color: "#34C759" },
  milestone: { label: "Milestone", color: "#E8A100" },
  care: { label: "Care", color: "#0A84FF" },
  system: { label: "System", color: "#8E8E93" },
};

export const SEVERITY_RANK = { p5: 1, p4: 2, p3: 3, p2: 4, p1: 5 };
const collectionRank = (p) => (p.status === "deceased" ? -1 : p.critical_active ? 10 + (SEVERITY_RANK[p.critical_severity] || 0) : 0);
export const sortCollection = (list) => [...list].sort((a, b) => collectionRank(b) - collectionRank(a));
const byName = (a, b) => (a.nickname || "").localeCompare(b.nickname || "", undefined, { sensitivity: "base", numeric: true });
const locRank = (l) => (l === "Graveyard" ? 2 : l ? 0 : 1);
export const sortPlants = (list, sortBy = "az", dueMap = {}) => {
  const dead = (p) => (p.status === "deceased" ? 1 : 0);
  const need = (p) => (p.critical_active ? 10 + (SEVERITY_RANK[p.critical_severity] || 0) : ({ overdue: 3, due: 2, soon: 1 })[dueMap[p.id]?.status] || 0);
  const loc = (p) => p.location?.trim() || "";
  const cmp = sortBy === "attention" ? (a, b) => need(b) - need(a) || byName(a, b)
    : sortBy === "newest" ? (a, b) => new Date(b.created_at) - new Date(a.created_at)
    : sortBy === "location" ? (a, b) => locRank(loc(a)) - locRank(loc(b)) || loc(a).localeCompare(loc(b)) || byName(a, b)
    : byName;
  return [...list].sort((a, b) => (sortBy === "location" ? 0 : dead(a) - dead(b)) || cmp(a, b));
};

export const PLANT_STATUS = {
  active: { label: "Active", color: "#30D158" },
  dormant: { label: "Dormant", color: "#8E8E93" },
  quarantine: { label: "Quarantine", color: "#FF9F0A" },
  gifted: { label: "Gifted", color: "#30B0C7" },
  sold: { label: "Sold", color: "#5E5CE6" },
  deceased: { label: "Deceased", color: "#636366" },
};

export const SOURCE_TYPES = {
  nursery: "Nursery", big_box: "Big-box store", online: "Online shop", trade: "Trade",
  gift: "Gift", rescue: "Rescue", propagation: "Propagation", other: "Other",
};

export const GROWTH_STAGES = { seedling: "Seedling", juvenile: "Juvenile", mature: "Mature", mother: "Mother plant" };

export const PROP_METHODS = {
  cutting: "Stem cutting", water: "Water propagation", division: "Division", leaf: "Leaf cutting",
  offset: "Offset / pup", air_layer: "Air layer", seed: "Seed", other: "Other",
};

export const PROP_STATUSES = [
  { key: "started", label: "Started", color: "#8E8E93" },
  { key: "rooting", label: "Rooting", color: "#0A84FF" },
  { key: "rooted", label: "Rooted", color: "#30B0C7" },
  { key: "potted", label: "Potted", color: "#AF52DE" },
  { key: "established", label: "Established", color: "#30D158" },
  { key: "failed", label: "Failed", color: "#FF453A" },
  { key: "promoted", label: "Promoted", color: "#34C759" },
];

export function ageOf(date) {
  if (!date) return null;
  const m = Math.floor((Date.now() - new Date(date).getTime()) / (30.44 * 86400000));
  if (m < 1) return "< 1 mo";
  if (m < 12) return `${m} mo`;
  const y = Math.floor(m / 12);
  return m % 12 ? `${y} yr ${m % 12} mo` : `${y} yr`;
}

export const money = (v) =>
  v === null || v === undefined || v === "" ? null : Number(v).toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });

export function relativeTime(iso) {
  if (!iso) return "";
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return formatDate(iso);
}

export function toLocalInput(date = new Date()) {
  const d = new Date(date);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

export const SEVERITY_META = {
  p1: { label: "Critical", color: "#E0242B", level: 1, hint: "May lose the plant. Act now and alert the team." },
  p2: { label: "Urgent", color: "#F26B0F", level: 2, hint: "Plant at risk. Treat today." },
  p3: { label: "Treat soon", color: "#D99A00", level: 3, hint: "Needs treatment within a few days." },
  p4: { label: "Monitor", color: "#2F7DF6", level: 4, hint: "Stable. Watch it and check again at follow-up." },
  p5: { label: "Watch", color: "#30A0B0", level: 5, hint: "Minor. Just keep an eye on it." },
};

export const SEVERITY_DEFAULT_LABELS = Object.fromEntries(Object.entries(SEVERITY_META).map(([k, v]) => [k, v.label]));
export const setSeverityLabels = (m = {}) => Object.keys(SEVERITY_META).forEach((k) => { SEVERITY_META[k].label = m[k] || SEVERITY_DEFAULT_LABELS[k]; });

export const sevMeta = (s) => SEVERITY_META[s] || { label: "Acute", color: "#E0242B", level: 1 };
export const isEscalation = (from, to) => (SEVERITY_RANK[to] || 0) > (SEVERITY_RANK[from] || 0);

export const HEALTH_STATUS = {
  thriving: { label: "Thriving", color: "#30D158" },
  healthy: { label: "Healthy", color: "#34C759" },
  fair: { label: "Fair", color: "#FFCC00" },
  struggling: { label: "Struggling", color: "#FF9F0A" },
  critical: { label: "Critical", color: "#FF3B30" },
  dormant: { label: "Dormant", color: "#8E8E93" },
};

export const TASK_PRIORITY = {
  low: { label: "Low", color: "#8E8E93" },
  normal: { label: "Normal", color: "#0A84FF" },
  medium: { label: "Medium", color: "#FFB800" },
  high: { label: "High", color: "#FF9F0A" },
  urgent: { label: "Urgent", color: "#FF453A" },
  critical: { label: "Critical", color: "#C70015" },
};

export function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export function formatDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export function daysAgo(iso) {
  if (!iso) return null;
  const diff = (Date.now() - new Date(iso).getTime()) / 86400000;
  return Math.floor(diff);
}


let NEW_DAYS = 30;
export const setNewDays = (n) => { NEW_DAYS = Number.isFinite(+n) ? +n : 30; };
export const isNewPlant = (p) => p?.status !== "deceased" && !p?.new_dismissed && !!p?.created_at && Date.now() - new Date(p.created_at).getTime() < NEW_DAYS * 86400000;
export const NEW_BADGE = "h-5 px-2 rounded-full bg-[#0A84FF]/10 text-[#0A84FF] text-[10px] font-semibold uppercase tracking-wider inline-flex items-center shrink-0";
